/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, array, bool, string, object } from 'prop-types';
import classNames from 'clsx';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import p from 'polyglot/polyglot';

import { getMessages as getMessagesAction } from 'actions/inbox';

import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import MessageCard from 'components/MessageCard/MessageCard';

import styles from './message-list.scss';

/**
 * This is the MessageList component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 * @author Niklas Rosdahl <niklas.rosdahl@vntrs.com>
 */

const TABS = [
  {
    title: p.t('inbox.tabs.job'),
    filter: 'JOB',
  },
  {
    title: p.t('inbox.tabs.group'),
    filter: 'GROUP',
  },
];
export class MessageList extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    match: shape({
      params: shape({
        discussionId: string,
      }).isRequired,
    }).isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: object.isRequired,
    messages: shape({
      loading: bool.isRequired,
      items: array.isRequired,
    }).isRequired,
    getMessages: func.isRequired,
  };

  constructor(props) {
    super(props);
    const { discussionId = '' } = this.props.match.params;

    this.state = {
      tab: discussionId.toUpperCase().includes(TABS[1].filter) ? 1 : 0,
    };
  }

  componentDidMount() {
    const {
      auth: { token },
      getMessages,
    } = this.props;
    const { tab } = this.state;

    getMessages(token, { filter: TABS[tab].filter });
  }

  getCurrentDiscussionId = () => {
    const {
      match: { params },
      messages: { items, loading },
    } = this.props;

    if (params.discussionId) {
      return params.discussionId;
    }

    if (!loading && items[0]) {
      return items[0].discussion_id;
    }
  };

  renderTabs = () => {
    const {
      auth: { token },
      getMessages,
    } = this.props;
    const { tab } = this.state;

    return TABS.map(({ title, filter }, index) => (
      <span
        key={`tab-${index}`}
        onClick={() =>
          this.setState({ tab: index }, () => {
            getMessages(token, { filter });
          })
        }
        className={classNames(styles.tab, {
          [styles.active]: tab === index,
        })}
      >
        {p.t(title)}
      </span>
    ));
  };

  /**
   * Render MessageList.
   *
   * @return {Object}
   */
  render() {
    const {
      user,
      messages: { items = [], loading },
    } = this.props;

    const currentDiscussionId = this.getCurrentDiscussionId();

    const { hired, closed } = items.reduce(
      (initial, item) => {
        if (
          item?.contract?.accepted_by_client &&
          item?.contract?.accepted_by_talent
        ) {
          if (item?.contract?.is_canceled) {
            return {
              ...initial,
              closed: [item.discussion_id, ...initial.closed],
            };
          }

          return {
            ...initial,
            hired: [item.discussion_id, ...initial.hired],
          };
        }

        return initial;
      },
      {
        hired: [],
        closed: [],
      }
    );

    return (
      <div className={styles.wrapper}>
        <div className={styles.tabs}>{this.renderTabs()}</div>

        {loading && (
          <div className={styles.spinner}>
            <SpinnerIcon small />
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className={styles.cardContainer}>
            {items.map(item => {
              return (
                <MessageCard
                  hired={hired.includes(item.discussion_id)}
                  closed={closed.includes(item.discussion_id)}
                  key={item.discussion_id}
                  user={user}
                  currentDiscussionId={currentDiscussionId}
                  {...item}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(
  connect(({ auth, user, messages }) => ({ auth, user, messages }), {
    getMessages: getMessagesAction,
  })(MessageList)
);
